// Shared entity, jurisdiction, and currency helpers used across pages.

// ── Currency ──────────────────────────────────────────────────────────
export const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };

// Fallback FX rates to EUR — used only for aggregation display when backend rates unavailable.
// These are approximate and should NOT be used for financial calculations.
// Live rates are fetched via /api/fx/rates and cached by the backend.
export const FX_TO_EUR = { EUR: 1, GBP: 1.17, USD: 0.92, DKK: 0.134 };

export const toEUR = (amounts) =>
  Object.entries(amounts).reduce((sum, [cur, amt]) => sum + amt * (FX_TO_EUR[cur] ?? 1), 0);

// Convert a multi-currency map { GBP: 100, EUR: 50 } to a single target currency total
export const convertToTarget = (amounts, targetCurrency) => {
  let totalInEUR = 0;
  for (const [cur, amt] of Object.entries(amounts)) {
    totalInEUR += amt * (FX_TO_EUR[cur] ?? 1);
  }
  const targetRate = FX_TO_EUR[targetCurrency] ?? 1;
  return totalInEUR / targetRate;
};

// ── Jurisdictions ─────────────────────────────────────────────────────
export const jurisdictions = [
  { key: 'UK',       name: 'United Kingdom', flag: '🇬🇧', color: '#2563EB', currency: 'GBP' },
  { key: 'France',   name: 'France',         flag: '🇫🇷', color: '#DC2626', currency: 'EUR' },
  { key: 'Personal', name: 'Personal',       flag: '👤', color: '#F59E0B', currency: 'EUR' },
];

export const jurisdictionsByKey = {};
jurisdictions.forEach((j) => { jurisdictionsByKey[j.key] = j; });

// ── Entity → Jurisdiction mapping ─────────────────────────────────────
// Map entity name → jurisdiction key (mirrors ENTITY_REGISTRY in config.py)
export const ENTITY_NAME_JURISDICTION = {
  'AFPV Invest': 'France', 'Bonjour Immobilier': 'France', 'SCI 113 Rue Saint-Honoré': 'France',
  'PFV Holding': 'France', 'Bonjour Investments SAS': 'France',
  'Bonjour Residences': 'UK', 'Bonjour Investments Ltd': 'UK', 'Bonjour Invest UK': 'UK',
  'Edinburgh Capital Investments': 'UK', 'High Street Residence': 'UK', 'Bonjour Holding': 'UK',
  'Personal': 'Personal', 'Fan Zhang and Piotr Kacki': 'Personal', 'Fan Zhang & Piotr Kacki': 'Personal', 'Fan Zhang': 'Personal', 'Piotr Kacki': 'Personal',
};

// Fallback: entity type code → jurisdiction (when entity_name isn't in the map)
export const ENTITY_TYPE_JURISDICTION = { br_ltd: 'UK', hsr: 'UK', bi_ltd: 'UK', bi_sas: 'France', bi_uk: 'UK', bh: 'UK', eci: 'UK', afpv: 'France', bi_immo: 'France', sci_113: 'France', pfv: 'France', fan_zhang: 'Personal', fan_zhang_piotr_kacki: 'Personal', piotr_kacki: 'Personal' };

// Fallback: entity type code → display name (fixes raw codes like "eci" showing as entity name)
export const ENTITY_TYPE_DISPLAY_NAME = {
  eci: 'Edinburgh Capital Investments', br_ltd: 'Bonjour Residences', hsr: 'High Street Residence',
  bi_ltd: 'Bonjour Investments Ltd', bi_sas: 'Bonjour Investments SAS',
  bi_uk: 'Bonjour Invest UK', bh: 'Bonjour Holding',
  afpv: 'AFPV Invest', bi_immo: 'Bonjour Immobilier', sci_113: 'SCI 113 Rue Saint-Honoré', pfv: 'PFV Holding',
  fan_zhang: 'Fan Zhang', fan_zhang_piotr_kacki: 'Fan Zhang and Piotr Kacki', piotr_kacki: 'Piotr Kacki', Personal: 'Fan Zhang and Piotr Kacki',
};

export const getJurisdictionKey = (entityName, entityType) =>
  ENTITY_NAME_JURISDICTION[entityName] || ENTITY_TYPE_JURISDICTION[entityType] || null;

// ── Entity dot colors (jurisdiction-derived) ──────────────────────────
export const JURISDICTION_DOT_COLORS = { UK: '#2563EB', France: '#DC2626', Personal: '#F59E0B' };

export const getEntityDotColor = (entityName, entityType) => {
  const jur = getJurisdictionKey(entityName, entityType);
  return JURISDICTION_DOT_COLORS[jur] || '#6B7280';
};

// ── Entity resolution ────────────────────────────────────────────────
/**
 * Resolve sidebar entity selection to backend-compatible entity ID.
 * Returns the UUID directly — the backend's resolve_entity_filter()
 * handles mapping UUIDs to type codes for querying.
 * @param {string} selectedEntity - 'all' or entity UUID from store
 * @param {Array} entities - entity objects from store (unused, kept for compat)
 * @returns {string|null} entity UUID or null for 'all'
 */
export const resolveEntityId = (selectedEntity, entities) => {
  if (!selectedEntity || selectedEntity === 'all') return null;
  return selectedEntity;
};

/**
 * Build a lookup that resolves an entity_id value by EITHER its UUID
 * (entities[].id, what GET /entities returns) OR its canonical-registry
 * slug (entities[].type, e.g. "eci"). Every live bank-feed writer stamps
 * entity_id as a slug, so a UUID-only lookup silently fails and renders
 * blank. Mirrors the entityByType fallback already applied in
 * DashboardPage.jsx ("handles bank accounts created before the Wise sync
 * was fixed to use real UUIDs") -- this is the same class of bug on the
 * Transactions/Reconciliation side, given one shared helper so the two
 * lookup tables can't drift apart the way DashboardPage.jsx's inline
 * copy could.
 * @param {Array} entities - entity objects from useEntityStore(), each
 *   with `.id` (UUID) and `.type` (slug).
 * @returns {(entityId: string) => object|undefined}
 */
export const buildEntityLookup = (entities) => {
  const byId = Object.fromEntries((entities || []).map((e) => [e.id, e]));
  const byType = {};
  for (const e of entities || []) {
    if (!byType[e.type]) byType[e.type] = e;
  }
  return (entityId) => byId[entityId] || byType[entityId];
};
